import { useState } from "react";
import T from "../theme/colors";

/**
 * Input — text, number, email, password, textarea, color.
 * Color type renders a swatch + hex text combo.
 */
export default function Input({
  label, value, onChange, onBlur, placeholder, type = "text",
  required, error, prefix, suffix, style = {}, disabled = false,
  hint, rows, tabIndex,
}) {
  const [focused, setFocused] = useState(false);
  const isTextarea = type === "textarea";
  const isColor = type === "color";

  // Phase 36da.45 — combined blur handler: keep the existing focused-style
  // toggle AND forward the blur event to a caller-supplied onBlur (used
  // e.g. by the packing-slip narration to apply auto-suffix on Tab).
  const handleBlur = (e) => {
    setFocused(false);
    if (typeof onBlur === "function") onBlur(e);
  };

  const boxStyle = {
    display: "flex", alignItems: isTextarea ? "flex-start" : "center", gap: 8,
    background: disabled ? "var(--bg-app)" : "var(--bg-input)",
    border: `1px solid ${focused ? "var(--border-focus)" : error ? "var(--danger)" : "var(--border)"}`,
    borderRadius: 7,
    padding: isTextarea ? "10px 12px" : isColor ? "5px 8px" : "0 12px",
    transition: "border-color 0.15s, box-shadow 0.15s",
    boxShadow: focused ? `0 0 0 3px var(--primary-dim)` : error ? `0 0 0 3px var(--danger-bg)` : "none",
  };

  const inputStyle = {
    background: "transparent", border: "none",
    padding: isTextarea ? "0" : "8px 0",
    flex: 1, color: disabled ? "var(--text-2)" : "var(--text-0)",
    fontSize: 15, fontFamily: "var(--font-ui)",
    resize: isTextarea ? "vertical" : "none",
    minHeight: isTextarea ? 80 : "auto",
    outline: "none",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, ...style }}>
      {label && (
        <label style={{ fontSize: 14, fontWeight: 500, color: "var(--text-1)", letterSpacing: "0.02em" }}>
          {label}
          {required && <span style={{ color: "var(--primary)", marginLeft: 2 }}>*</span>}
        </label>
      )}

      {isColor ? (
        /* ── Color picker: swatch + text ── */
        <div style={boxStyle}>
          <input
            type="color"
            value={value || "#000000"}
            onChange={e => onChange(e.target.value)}
            disabled={disabled}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            style={{
              width: 28, height: 28, borderRadius: 5, border: `1px solid var(--border)`,
              cursor: disabled ? "not-allowed" : "pointer",
              padding: 1, background: "none", flexShrink: 0,
            }}
          />
          <input
            type="text"
            value={value || ""}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder || "#C95210"}
            disabled={disabled}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            style={{ ...inputStyle, fontFamily: "monospace", fontSize: 14 }}
          />
        </div>
      ) : (
        <div style={boxStyle}>
          {prefix && (
            <span style={{ color: "var(--text-2)", fontSize: 15, flexShrink: 0, paddingTop: isTextarea ? 2 : 0 }}>
              {prefix}
            </span>
          )}
          {isTextarea
            ? <textarea
              value={value}
              onChange={e => onChange(e.target.value)}
              placeholder={placeholder}
              rows={rows || 3}
              disabled={disabled}
              onFocus={() => setFocused(true)}
              onBlur={handleBlur}
              tabIndex={tabIndex}
              style={inputStyle}
            />
            : <input
              type={type}
              value={value}
              onChange={e => onChange(e.target.value)}
              placeholder={placeholder}
              disabled={disabled}
              onFocus={() => setFocused(true)}
              onBlur={handleBlur}
              tabIndex={tabIndex}
              style={inputStyle}
            />
          }
          {suffix && (
            <span style={{ color: "var(--text-2)", fontSize: 15, flexShrink: 0 }}>{suffix}</span>
          )}
        </div>
      )}

      {error && <span style={{ fontSize: 13, color: "var(--danger)", display: "flex", alignItems: "center", gap: 4 }}>⚠ {error}</span>}
      {hint && !error && <span style={{ fontSize: 13, color: "var(--text-2)" }}>{hint}</span>}
    </div>
  );
}